/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { Navigate, Route } from "react-router-dom";

// components
import PrivateRoute from "./PrivateRoute";
import PermissionRoute from "./PermissionRoute";
import TicketsApp from "../pages/apps/Tickets";
import LoansManagement from "@/pages/admin/LoansManagement";
import ProspectiveMemberManagement from "@/pages/admin/ProspectiveMemberManagement";
import LoansCreate from "@/pages/admin/LoansManagement/Create";
import PrincipalSaving from "@/pages/admin/Deposit/PrincipalSavings";
import MandatorySaving from "@/pages/admin/Deposit/MandatorySavings";
import ListMandatorySaving from "@/pages/admin/Deposit/MandatorySavings/list";
import ListPrincipalSaving from "@/pages/admin/Deposit/PrincipalSavings/list";
import VoluntarySavings from "@/pages/admin/Deposit/VoluntarySavings";
import ListVoluntarySavings from "@/pages/admin/Deposit/VoluntarySavings/list";
import LoanApplication from "@/pages/admin/LoansManagement/LoansApplication";
import LoansValidation from "@/pages/admin/LoansManagement/LoansValidation";
import LoansPayment from "@/pages/admin/LoansManagement/LoansPayment";
import LoansDisburse from "@/pages/admin/LoansManagement/LoansDisburse";
import LoansActive from "@/pages/admin/LoansManagement/LoansActive";
import LoansCancelled from "@/pages/admin/LoansManagement/LoansCancelled";
import LoansCompleted from "@/pages/admin/LoansManagement/LoansCompleted";
import LoansDisburseDetail from "@/pages/admin/LoansManagement/LoansDisburse/detail";
import LoansCompletedDetail from "@/pages/admin/LoansManagement/LoansCompleted/detail";
import LoansActiveDetail from "@/pages/admin/LoansManagement/LoansActive/detail";
import LoansValidationDetail from "@/pages/admin/LoansManagement/LoansValidation/detail";
import LoansCancelledDetail from "@/pages/admin/LoansManagement/LoansCancelled/detail";
import VoluntarySavingDetail from "@/pages/admin/Deposit/VoluntarySavings/list/detail";
import PrincipalSavingDetail from "@/pages/admin/Deposit/PrincipalSavings/list/detail";
import MandatorySavingDetail from "@/pages/admin/Deposit/MandatorySavings/list/detail";
import GeneralLedger from "@/pages/admin/Report/GeneralLedger";
import BalanceSheet from "@/pages/admin/Report/BalanceSheet";
import TrialBalance from "@/pages/admin/Report/TrialBalance";
import IncomeStatement from "@/pages/admin/Report/IncomeStatement";
import Ledger from "@/pages/admin/Report/Ledger";
import CashBook from "@/pages/admin/Report/CashBook";
import ChartOfAccount from "@/pages/admin/Report/ChartOfAccount";
import ClosingPeriod from "@/pages/admin/Report/ClosingPeriod";
import ManualJournal from "@/pages/admin/Accounting/ManualJournal";

// SHU (Sisa Hasil Usaha / Profit Distribution)
import { SHUCalculationList, CreateSHUCalculation, SHUCalculationDetail } from "@/pages/admin/SHU";

// Deposit Management System
const CooperativeDepositDashboard = React.lazy(() => import("@/pages/admin/DepositManagement/CooperativeDepositDashboard"));
const CreateTopupRequest = React.lazy(() => import("@/pages/admin/DepositManagement/CreateTopupRequest"));
const TopupRequestsList = React.lazy(() => import("@/pages/admin/DepositManagement/TopupRequestsList"));
const TransactionHistory = React.lazy(() => import("@/pages/admin/DepositManagement/TransactionHistory"));
const DepositAlerts = React.lazy(() => import("@/pages/admin/DepositManagement/DepositAlerts"));
const SuperAdminTopupManagement = React.lazy(() => import("@/pages/admin/DepositManagement/SuperAdminTopupManagement"));

// Notifications
const NotificationsPage = React.lazy(() => import("@/pages/admin/Notifications"));

// PPOB Management System
const MasterProductsManagement = React.lazy(() => import("@/pages/admin/PPOB/Superadmin/MasterProductsManagement"));
const CooperativeProductsManagement = React.lazy(() => import("@/pages/admin/PPOB/Cooperative/CooperativeProductsManagement"));
const PPOBTransactionHistory = React.lazy(() => import("@/pages/admin/PPOB/Transactions/TransactionHistory"));
const PPOBConfig = React.lazy(() => import("@/pages/admin/PPOB/Settings/PPOBConfig"));
const PrepaidTransaction = React.lazy(() => import("@/pages/admin/PPOB/Transactions/PrepaidTransaction"));
const PostpaidTransaction = React.lazy(() => import("@/pages/admin/PPOB/Transactions/PostpaidTransaction"));
const TransactionDetail = React.lazy(() => import("@/pages/admin/PPOB/Transactions/TransactionDetail"));

// settings
const CooperativeGeneralSettings = React.lazy(() => import("@/pages/settings/Cooperative/GeneralInfo"));
const AccountingSettings = React.lazy(() => import("@/pages/settings/Cooperative/AccountingSettings"));
const MembershipSettings = React.lazy(() => import("@/pages/settings/Cooperative/MembershipSettings"));
const OutletSettings = React.lazy(() => import("@/pages/settings/Cooperative/OutletSettings"));
const SavingsSettings = React.lazy(() => import("@/pages/settings/Cooperative/SavingsSettings"));
const LoanSettings = React.lazy(() => import("@/pages/settings/Cooperative/LoanSettings"));
const SHUSettings = React.lazy(() => import("@/pages/settings/Cooperative/SHUSettings"));

// lazy load all the views

// auth
const Login = React.lazy(() => import("../pages/auth/Login"));
const Register = React.lazy(() => import("../pages/auth/Register"));
const CooperativeRegister = React.lazy(() => import("../pages/auth/CooperativeRegister"));
const RecoverPassword = React.lazy(() => import("../pages/auth/RecoverPassword"));
const LockScreen = React.lazy(() => import("../pages/auth/LockScreen"));

// dashboards
const Dashboard = React.lazy(() => import("../pages/dashboard/"));
const CooperativeDashboard = React.lazy(() => import("../pages/dashboard/CooperativeDashboard"));
const SuperAdminDashboard = React.lazy(() => import("../pages/dashboard/SuperAdminDashboard"));
const OrdersProductsDashboard = React.lazy(() => import("../pages/dashboard/OrdersProductsDashboard"));
const CooperativeHealthDashboard = React.lazy(() => import("../pages/dashboard/CooperativeHealthDashboard"));
const BupatiDashboard = React.lazy(() => import("../pages/dashboard/BupatiDashboard"));

// apps 
const CalendarApp = React.lazy(() => import('../pages/apps/Calendar'));
const FileManagerApp = React.lazy(() => import('../pages/apps/FileManager'));
const KanbanApp = React.lazy(() => import('../pages/apps/Kanban'));
const NewsApp = React.lazy(() => import('../pages/apps/News'));
const TrainingApp = React.lazy(() => import('../pages/apps/Training'));
const BankTransferApp = React.lazy(() => import('../pages/apps/BankTransfer'));
const ProjectCreate = React.lazy(() => import('../pages/apps/Project/Create'));
const ProjectDetail = React.lazy(() => import('../pages/apps/Project/Detail'));
const ProjectList = React.lazy(() => import('../pages/apps/Project/List'));
const CashierPOS = React.lazy(() => import('../pages/apps/Cashier'));

// cooperative outlet
const CooperativeOutletOrderList = React.lazy(() => import('../pages/apps/CooperativeOutlet/Orders/List'));
const CooperativeOutletCreateOrder = React.lazy(() => import('../pages/apps/CooperativeOutlet/Orders/Create'));
const CooperativeOutletOrderDetails = React.lazy(() => import('../pages/apps/CooperativeOutlet/Orders/Details'));
const CooperativeOutletOrderEdit = React.lazy(() => import('../pages/apps/CooperativeOutlet/Orders/Edit'));
const CooperativeOutletTransactionHistory = React.lazy(() => import('../pages/apps/CooperativeOutlet/Orders/History'));
const CooperativeOutletProductManagement = React.lazy(() => import('../pages/apps/CooperativeOutlet/Products'));
const CooperativeOutletProductAdd = React.lazy(() => import('../pages/apps/CooperativeOutlet/Products/Add'));
const CooperativeOutletProductEdit = React.lazy(() => import('../pages/apps/CooperativeOutlet/Products/Edit'));
const CooperativeOutletProductView = React.lazy(() => import('../pages/apps/CooperativeOutlet/Products/View'));
const CooperativeOutletCategories = React.lazy(() => import('../pages/apps/CooperativeOutlet/Products/Categories'));
const CooperativeOutletInventory = React.lazy(() => import('../pages/apps/CooperativeOutlet/Products/Inventory'));
const CooperativeOutletFeaturedProducts = React.lazy(() => import('../pages/apps/CooperativeOutlet/Products/Featured'));
const CooperativeOutletSalesDiscounts = React.lazy(() => import('../pages/apps/CooperativeOutlet/Products/Discounts'));
const CooperativeOutletProductReports = React.lazy(() => import('../pages/apps/CooperativeOutlet/Products/Reports'));

// Supplier Management
const CooperativeOutletSupplierList = React.lazy(() => import('../pages/apps/CooperativeOutlet/Suppliers/List'));
const CooperativeOutletSupplierCreate = React.lazy(() => import('../pages/apps/CooperativeOutlet/Suppliers/Create'));
const CooperativeOutletSupplierEdit = React.lazy(() => import('../pages/apps/CooperativeOutlet/Suppliers/Edit'));
const CooperativeOutletSupplierView = React.lazy(() => import('../pages/apps/CooperativeOutlet/Suppliers/View'));

// Inventory Management
const InventoryStockSummary = React.lazy(() => import('../pages/apps/CooperativeOutlet/Inventory/StockSummary'));
const InventoryMovements = React.lazy(() => import('../pages/apps/CooperativeOutlet/Inventory/Movements'));
const InventoryAdjustmentList = React.lazy(() => import('../pages/apps/CooperativeOutlet/Inventory/AdjustmentList'));
const InventoryCreateAdjustment = React.lazy(() => import('../pages/apps/CooperativeOutlet/Inventory/CreateAdjustment'));
const InventoryAdjustmentDetail = React.lazy(() => import('../pages/apps/CooperativeOutlet/Inventory/AdjustmentDetail'));

// Purchase Order Management
const PurchaseOrderList = React.lazy(() => import('../pages/apps/CooperativeOutlet/PurchaseOrders/List'));
const PurchaseOrderCreate = React.lazy(() => import('../pages/apps/CooperativeOutlet/PurchaseOrders/Create'));
const PurchaseOrderDetail = React.lazy(() => import('../pages/apps/CooperativeOutlet/PurchaseOrders/Detail'));
const PurchaseOrderReceiveGoods = React.lazy(() => import('../pages/apps/CooperativeOutlet/PurchaseOrders/ReceiveGoods'));

// admin
const RolePermissionManagement = React.lazy(() => import('../pages/admin/RolePermissions'));
const MenuPermissionManagement = React.lazy(() => import('../pages/admin/MenuPermissions'));
const UserManagement = React.lazy(() => import('../pages/admin/UserManagement'));
const MemberManagement = React.lazy(() => import('../pages/admin/MemberManagement'));
const MemberDetail = React.lazy(() => import('../pages/admin/MemberManagement/Detail'));
const MemberEdit = React.lazy(() => import('../pages/admin/MemberManagement/Edit'));
const MemberCreate = React.lazy(() => import('../pages/admin/MemberManagement/Create'));
const BankTransferTypeManagement = React.lazy(() => import('../pages/admin/BankTransferTypeManagement'));
const CooperativeManagement = React.lazy(() => import('../pages/admin/CooperativeManagement'));
const CooperativeCreate = React.lazy(() => import('../pages/admin/CooperativeManagement/Create'));
const CooperativeDetail = React.lazy(() => import('../pages/admin/CooperativeManagement/Detail'));
const CooperativeEdit = React.lazy(() => import('../pages/admin/CooperativeManagement/Edit'));

// extra pages 
const Starter = React.lazy(() => import('../pages/extra/Starter'));
const Timeline = React.lazy(() => import('../pages/extra/TimeLine'));
const Invoice = React.lazy(() => import('../pages/extra/Invoice'));
const Gallery = React.lazy(() => import('../pages/extra/Gallery'));
const FAQs = React.lazy(() => import('../pages/extra/FAQs'));
const Pricing = React.lazy(() => import('../pages/extra/Pricing'));
const Profile = React.lazy(() => import('../pages/pages/Profile'));

// legal pages
const PrivacyPolicy = React.lazy(() => import('../pages/legal/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('../pages/legal/TermsOfService'));
const DataDeletion = React.lazy(() => import('../pages/legal/DataDeletion'));

// error pages
const Maintenance = React.lazy(() => import('../pages/error/Maintenance'));
const ComingSoon = React.lazy(() => import('../pages/error/ComingSoon'));
const Error404 = React.lazy(() => import('../pages/error/Error404'));
const Error404Alt = React.lazy(() => import('../pages/error/Error404Alt'));
const Error403 = React.lazy(() => import('../pages/error/Error403'));
const Error500 = React.lazy(() => import('../pages/error/Error500'));

// base ui
const Accordions = React.lazy(() => import('../pages/ui/Accordions'));
const Alerts = React.lazy(() => import('../pages/ui/Alerts'));
const Avatars = React.lazy(() => import('../pages/ui/Avatars'));
const Buttons = React.lazy(() => import('../pages/ui/Buttons'));
const Badges = React.lazy(() => import('../pages/ui/Badges'));
const Breadcrumb = React.lazy(() => import('../pages/ui/Breadcrumb'));
const Cards = React.lazy(() => import('../pages/ui/Cards'));
const Collapse = React.lazy(() => import('../pages/ui/Collapse'));
const Dismissible = React.lazy(() => import('../pages/ui/Dismissible'));
const Dropdowns = React.lazy(() => import('../pages/ui/Dropdowns'));
const Progress = React.lazy(() => import('../pages/ui/Progress'));
const Skeleton = React.lazy(() => import('../pages/ui/Skeleton'));
const Spinners = React.lazy(() => import('../pages/ui/Spinners'));
const ListGroup = React.lazy(() => import('../pages/ui/ListGroup'));
const Ratio = React.lazy(() => import('../pages/ui/Ratio'));
const Tabs = React.lazy(() => import('../pages/ui/Tabs'));
const Modals = React.lazy(() => import('../pages/ui/Modals'));
const Offcanvas = React.lazy(() => import('../pages/ui/Offcanvas'));
const Popovers = React.lazy(() => import('../pages/ui/Popovers'));
const Tooltips = React.lazy(() => import('../pages/ui/Tooltips'));
const Typography = React.lazy(() => import('../pages/ui/Typography'));

// extended ui
const Swiper = React.lazy(() => import('../pages/extended/Swiper'));
const NestableList = React.lazy(() => import('../pages/extended/NestableList'));
const Ratings = React.lazy(() => import('../pages/extended/Ratings'));
const Animation = React.lazy(() => import('../pages/extended/Animation'));
const Player = React.lazy(() => import('../pages/extended/Player'));
const Scrollbar = React.lazy(() => import('../pages/extended/Scrollbar'));
const SweetAlert = React.lazy(() => import('../pages/extended/SweetAlert'));
const TourPage = React.lazy(() => import('../pages/extended/TourPage'));
const TippyTooltip = React.lazy(() => import('../pages/extended/TippyTooltip'));
const Lightbox = React.lazy(() => import('../pages/extended/Lightbox'));

// forms
const FormElements = React.lazy(() => import('../pages/forms/FormElements'));
const FormSelect = React.lazy(() => import('../pages/forms/Select'));
const Range = React.lazy(() => import('../pages/forms/Range'));
const Pickers = React.lazy(() => import('../pages/forms/Pickers'));
const Masks = React.lazy(() => import('../pages/forms/Masks'));
const Editor = React.lazy(() => import('../pages/forms/Editor'));
const FileUploads = React.lazy(() => import('../pages/forms/FileUploads'));
const Validation = React.lazy(() => import('../pages/forms/Validation'));
const FormLayout = React.lazy(() => import('../pages/forms/FormLayout'));

// tables
const BasicTables = React.lazy(() => import('../pages/tables/BasicTables'));
const DataTables = React.lazy(() => import('../pages/tables/DataTables'));

// icons
const MingCuteIcons = React.lazy(() => import('../pages/ui/icons/MingCuteIcons'));
const FeatherIcons = React.lazy(() => import('../pages/ui/icons/FeatherIcons'));
const MaterialSymbolIcons = React.lazy(() => import('../pages/ui/icons/MaterialSymbolIcons'));

// chart
const Chart = React.lazy(() => import('../pages/ui/Chart'));

// maps
const VectorMaps = React.lazy(() => import('../pages/ui/maps/VectorMaps'));
const GoogleMaps = React.lazy(() => import('../pages/ui/maps/GoogleMaps'));

// dashboards
const dashboardRoutes = {
  path: "/home",
  name: "Dashboards",
  icon: "home",
  header: "Navigation",
  children: [
    {
      path: "/",
      name: "Root",
      element: <Navigate to='/dashboard' />,
      route: PrivateRoute,
    },
    {
      path: '/dashboard',
      name: "Dashboard",
      element: <Dashboard />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative/dashboard',
      name: "Cooperative Dashboard",
      element: <CooperativeDashboard />,
      route: PermissionRoute,
    },
    {
      path: '/super-admin/dashboard',
      name: "Super Admin Dashboard",
      element: <SuperAdminDashboard />,
      route: PermissionRoute,
    },
    {
      path: '/orders-products/dashboard',
      name: "Orders & Products Dashboard",
      element: <OrdersProductsDashboard />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-health/dashboard',
      name: "Cooperative Health Dashboard",
      element: <CooperativeHealthDashboard />,
      route: PermissionRoute,
    },
    {
      path: '/bupati-dashboard',
      name: "Dashboard Bupati",
      element: <BupatiDashboard />,
      route: PermissionRoute,
    },
  ],
};

// Apps
const calendarAppRoutes = {
  path: "/apps/calendar",
  name: "Calendar",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "calendar",
  element: <CalendarApp />,
  header: "Apps",
};

const ticketsAppRoutes = {
  path: "/apps/tickets",
  name: "Tickets",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "tickets",
  element: <TicketsApp />,
  header: "Apps",
};

const fileAppRoutes = {
  path: "/apps/file-manager",
  name: "File Manager",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "filemanager",
  element: <FileManagerApp />,
  header: "Apps",
};

const kanbanAppRoutes = {
  path: "/apps/kanban",
  name: "Kanban Board",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "kanban",
  element: <KanbanApp />,
  header: "Apps",
};

const newsAppRoutes = {
  path: "/apps/news",
  name: "News Management",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "news",
  element: <NewsApp />,
  header: "Apps",
};

const trainingAppRoutes = {
  path: "/apps/training",
  name: "Training Management",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "training",
  element: <TrainingApp />,
  header: "Apps",
};

const bankTransferAppRoutes = {
  path: "/apps/bank-transfer",
  name: "Bank Transfer Management",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "bank",
  element: <BankTransferApp />,
  header: "Apps",
};

const projectAppRoutes = {
  path: "/apps/project",
  name: "Project",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "project",
  children: [
    {
      path: '/apps/project/list',
      name: 'ProjectList',
      element: <ProjectList />,
      route: PermissionRoute,
    },
    {
      path: '/apps/project/detail',
      name: 'ProjectDetail',
      element: <ProjectDetail />,
      route: PermissionRoute,
    },
    {
      path: '/apps/project/create',
      name: 'ProjectCreate',
      element: <ProjectCreate />,
      route: PermissionRoute,
    },  ]
};

const cooperativeOutletAppRoutes = {
  path: "/cooperative-outlet",
  name: "Cooperative Outlet",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "cooperative-outlet",
  children: [
    {
      path: '/cooperative-outlet/orders',
      name: 'OrderList',
      element: <CooperativeOutletOrderList />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/orders/create',
      name: 'CreateOrder',
      element: <CooperativeOutletCreateOrder />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/orders/:orderId/details',
      name: 'OrderDetails',
      element: <CooperativeOutletOrderDetails />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/orders/:orderId/edit',
      name: 'OrderEdit',
      element: <CooperativeOutletOrderEdit />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/orders/history',
      name: 'TransactionHistory',
      element: <CooperativeOutletTransactionHistory />,
      route: PermissionRoute,
    },    {
      path: '/cooperative-outlet/products',
      name: 'ProductManagement',
      element: <CooperativeOutletProductManagement />,
      route: PermissionRoute,    },    {
      path: '/cooperative-outlet/products/add',
      name: 'AddProduct',
      element: <CooperativeOutletProductAdd />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/products/:id/edit',
      name: 'EditProduct',
      element: <CooperativeOutletProductEdit />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/products/:id',
      name: 'ViewProduct',
      element: <CooperativeOutletProductView />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/products/categories',
      name: 'Categories',
      element: <CooperativeOutletCategories />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/products/inventory',
      name: 'Inventory',
      element: <CooperativeOutletInventory />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/products/featured',
      name: 'FeaturedProducts',
      element: <CooperativeOutletFeaturedProducts />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/products/discounts',
      name: 'SalesDiscounts',
      element: <CooperativeOutletSalesDiscounts />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/products/reports',
      name: 'ProductReports',
      element: <CooperativeOutletProductReports />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/suppliers',
      name: 'SupplierList',
      element: <CooperativeOutletSupplierList />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/suppliers/create',
      name: 'CreateSupplier',
      element: <CooperativeOutletSupplierCreate />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/suppliers/:id/edit',
      name: 'EditSupplier',
      element: <CooperativeOutletSupplierEdit />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/suppliers/:id',
      name: 'ViewSupplier',
      element: <CooperativeOutletSupplierView />,
      route: PermissionRoute,
    },
    // Inventory Routes
    {
      path: '/cooperative-outlet/inventory/stock-summary',
      name: 'StockSummary',
      element: <InventoryStockSummary />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/inventory/movements',
      name: 'InventoryMovements',
      element: <InventoryMovements />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/inventory/adjustments',
      name: 'AdjustmentList',
      element: <InventoryAdjustmentList />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/inventory/adjustments/create',
      name: 'CreateAdjustment',
      element: <InventoryCreateAdjustment />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/inventory/adjustments/:id',
      name: 'AdjustmentDetail',
      element: <InventoryAdjustmentDetail />,
      route: PermissionRoute,
    },
    // Purchase Order Routes
    {
      path: '/cooperative-outlet/purchase-orders',
      name: 'PurchaseOrderList',
      element: <PurchaseOrderList />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/purchase-orders/create',
      name: 'CreatePurchaseOrder',
      element: <PurchaseOrderCreate />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/purchase-orders/:id',
      name: 'PurchaseOrderDetail',
      element: <PurchaseOrderDetail />,
      route: PermissionRoute,
    },
    {
      path: '/cooperative-outlet/purchase-orders/:id/receive',
      name: 'ReceiveGoods',
      element: <PurchaseOrderReceiveGoods />,
      route: PermissionRoute,
    },
  ]
};

const adminRoutes = {
  path: "/admin",
  name: "Admin",
  header: "Administration",
  icon: "mgc_shield_line",
  children: [
    {
      path: '/admin/cooperatives',
      name: 'Cooperative Management',
      element: <CooperativeManagement />,
      route: PermissionRoute,
    },
    {
      path: '/admin/cooperatives/create',
      name: 'Create Cooperative',
      element: <CooperativeCreate />,
      route: PermissionRoute,
    },
    {
      path: '/admin/cooperatives/:id',
      name: 'Cooperative Detail',
      element: <CooperativeDetail />,
      route: PermissionRoute,
    },
    {
      path: '/admin/cooperatives/:id/edit',
      name: 'Edit Cooperative',
      element: <CooperativeEdit />,
      route: PermissionRoute,
    },
    {
      path: '/admin/roles',
      name: 'Roles & Permissions',
      element: <RolePermissionManagement />,
      route: PermissionRoute,
    },
    {
      path: '/admin/menu-permissions',
      name: 'Menu Permissions',
      element: <MenuPermissionManagement />,
      route: PermissionRoute,
    },
    {
      path: '/admin/users',
      name: 'User Management',
      element: <UserManagement />,
      route: PermissionRoute,
    },
    {
      path: '/admin/bank-transfer-types',
      name: 'Bank Transfer Types',
      element: <BankTransferTypeManagement />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans',
      name: 'Loans Management',
      element: <LoansManagement />,
      route: PermissionRoute,
    },
    {
      path: '/loans/create',
      name: 'Create Loans',
      element: <LoansCreate />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loan-application',
      name: 'Loans Application Management',
      element: <LoanApplication />,
      route: PermissionRoute,
    },
    {
      path: '/admin/list-loans-application',
      name: 'List Loans Application',
      element: <LoansValidation />,
      route: PermissionRoute,
    },
    {
      path: '/admin/list-loans-application/:id/detail',
      name: 'Loans Validation Detail',
      element: <LoansValidationDetail />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans-disburse',
      name: 'Loans Disburse',
      element: <LoansDisburse />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans-disburse/:id/detail',
      name: 'Loans Disburse Detail',
      element: <LoansDisburseDetail />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans-active',
      name: 'List Loans Active',
      element: <LoansActive />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans-active/:id/detail',
      name: 'Loans Active Detail',
      element: <LoansActiveDetail />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans-cancelled',
      name: 'List Loans Cancelled',
      element: <LoansCancelled />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans-cancelled/:id/detail',
      name: 'Loans Cancelled Detail',
      element: <LoansCancelledDetail />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans-completed',
      name: 'List Loans Completed',
      element: <LoansCompleted />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans-completed/:id/detail',
      name: 'Loans Completed',
      element: <LoansCompletedDetail />,
      route: PermissionRoute,
    },
    {
      path: '/admin/loans-payment',
      name: 'Loans Payment',
      element: <LoansPayment />,
      route: PermissionRoute,
    },
    {
      path: '/admin/members',
      name: 'Member Management',
      element: <MemberManagement />,
      route: PermissionRoute,
    },
    {
      path: 'admin/prospective-members',
      name: 'Prospective Member Management',
      element: <ProspectiveMemberManagement />,
      route: PermissionRoute,
    },
    {
      path: '/admin/members/create',
      name: 'Create Member',
      element: <MemberCreate />,
      route: PermissionRoute,
    },
    {
      path: '/admin/members/:id/edit',
      name: 'Edit Member',
      element: <MemberEdit />,
      route: PermissionRoute,
    },
    {
      path: '/admin/members/:id',
      name: 'Member Detail',
      element: <MemberDetail />,
      route: PermissionRoute,
    },
    {
      path: '/notifications',
      name: 'Notifikasi',
      element: <NotificationsPage />,
      route: PrivateRoute,
    },
    {
      path: '/admin/deposit-principal-savings/',
      name: 'Deposit Principal Savings',
      element: <PrincipalSaving />,
      route: PermissionRoute,
    },
    {
      path: '/admin/deposit-mandatory-savings/',
      name: 'Deposit Mandatory Savings',
      element: <MandatorySaving />,
      route: PermissionRoute,
    },
    {
      path: '/admin/deposit-voluntary-savings/',
      name: 'Deposit Voluntary Savings',
      element: <VoluntarySavings />,
      route: PermissionRoute,
    },
    {
      path: '/admin/list-principal-savings',
      name: 'Deposit Principal Savings',
      element: <ListPrincipalSaving />,
      route: PermissionRoute,
    },
    {
      path: '/admin/list-principal-savings/:id/detail',
      name: 'Deposit Principal Savings Detail',
      element: <PrincipalSavingDetail />,
      route: PermissionRoute,
    },
    {
      path: '/admin/list-mandatory-savings',
      name: 'Deposit Mandatory Savings',
      element: <ListMandatorySaving />,
      route: PermissionRoute,
    },
    {
      path: '/admin/list-mandatory-savings/:id/detail',
      name: 'Deposit Mandatory Savings Detail',
      element: <MandatorySavingDetail />,
      route: PermissionRoute,
    },
    {
      path: '/admin/list-voluntary-savings',
      name: 'Deposit Voluntary Savings',
      element: <ListVoluntarySavings />,
      route: PermissionRoute,
    },
    {
      path: '/admin/list-voluntary-savings/:id/detail',
      name: 'Deposit Voluntary Savings Detail',
      element: <VoluntarySavingDetail />,
      route: PermissionRoute,
    },
    {
      path: '/admin/report/general-ledger',
      name: 'Jurnal Umum',
      element: <GeneralLedger />,
      route: PermissionRoute,
    },
    {
      path: '/admin/report/general-ledger/add',
      name: 'Buat Jurnal Manual',
      element: <ManualJournal />,
      route: PermissionRoute,
    },
    {
      path: '/admin/report/balance-sheet',
      name: 'Neraca',
      element: <BalanceSheet />,
      route: PermissionRoute,
    },
    {
      path: '/admin/report/trial-balance-complete',
      name: 'Neraca Saldo',
      element: <TrialBalance />,
      route: PermissionRoute,
    },
    {
      path: '/admin/report/income-statement',
      name: 'Laporan Laba Rugi',
      element: <IncomeStatement />,
      route: PermissionRoute,
    },
    {
      path: '/admin/report/cash-book',
      name: 'Cash Book',
      element: <CashBook />,
      route: PermissionRoute,
    },
    {
      path: '/admin/report/chart-of-accounts',
      name: 'Chart Of Account',
      element: <ChartOfAccount />,
      route: PermissionRoute,
    },
    {
      path: '/admin/report/ledger',
      name: 'Buku Besar',
      element: <Ledger />,
      route: PermissionRoute,
    },
    {
      path: '/admin/report/closing-period',
      name: 'Tutup Buku',
      element: <ClosingPeriod />,
      route: PermissionRoute,
    },
    {
      path: '/admin/shu',
      name: 'Daftar SHU',
      element: <SHUCalculationList />,
      route: PermissionRoute,
    },
    {
      path: '/admin/shu/create',
      name: 'Buat Perhitungan SHU',
      element: <CreateSHUCalculation />,
      route: PermissionRoute,
    },
    {
      path: '/admin/shu/calculations/:id',
      name: 'Detail Perhitungan SHU',
      element: <SHUCalculationDetail />,
      route: PermissionRoute,
    },
    // Deposit Management System - Cooperative Admin Routes
    {
      path: '/deposit',
      name: 'Dashboard Deposit Koperasi',
      element: <CooperativeDepositDashboard />,
      route: PermissionRoute,
    },
    {
      path: '/deposit/topup/create',
      name: 'Buat Permintaan Top Up',
      element: <CreateTopupRequest />,
      route: PermissionRoute,
    },
    {
      path: '/deposit/topup/requests',
      name: 'Daftar Permintaan Top Up',
      element: <TopupRequestsList />,
      route: PermissionRoute,
    },
    {
      path: '/deposit/transactions',
      name: 'Riwayat Transaksi Deposit',
      element: <TransactionHistory />,
      route: PermissionRoute,
    },
    {
      path: '/deposit/alerts',
      name: 'Notifikasi Deposit',
      element: <DepositAlerts />,
      route: PermissionRoute,
    },
    // Deposit Management System - Super Admin Route
    {
      path: '/admin/deposits/topup-management',
      name: 'Manajemen Top Up Deposit',
      element: <SuperAdminTopupManagement />,
      route: PermissionRoute,
    },
  ]
};

const cashierAppRoutes = {
  path: "/cashier",
  name: "Cashier POS",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "cashier",
  element: <CashierPOS />,
  header: "Apps",
};

// PPOB Management Routes
const ppobAppRoutes = {
  path: "/ppob",
  name: "PPOB",
  icon: "phone",
  header: "Apps",
  children: [
    {
      path: "/ppob/config",
      name: "Konfigurasi PPOB",
      element: <PPOBConfig />,
      route: PermissionRoute,
      roles: ["Super Admin"],
    },
    {
      path: "/ppob/master-products",
      name: "Master Produk PPOB",
      element: <MasterProductsManagement />,
      route: PermissionRoute,
      roles: ["Super Admin"],
    },
    {
      path: "/ppob/cooperative-products",
      name: "Produk PPOB Koperasi",
      element: <CooperativeProductsManagement />,
      route: PermissionRoute,
      roles: ["Admin"],
    },
    {
      path: "/ppob/prepaid",
      name: "Transaksi Prepaid",
      element: <PrepaidTransaction />,
      route: PermissionRoute,
      roles: ["Admin"],
    },
    {
      path: "/ppob/postpaid",
      name: "Transaksi Postpaid",
      element: <PostpaidTransaction />,
      route: PermissionRoute,
      roles: ["Admin"],
    },
    {
      path: "/ppob/transactions",
      name: "Riwayat Transaksi PPOB",
      element: <PPOBTransactionHistory />,
      route: PermissionRoute,
      roles: ["Admin", "Super Admin"],
    },
    {
      path: "/ppob/transactions/:id",
      name: "Detail Transaksi PPOB",
      element: <TransactionDetail />,
      route: PermissionRoute,
      roles: ["Admin", "Super Admin"],
    },
  ]
};

const appRoutes = [calendarAppRoutes, ticketsAppRoutes, projectAppRoutes, cooperativeOutletAppRoutes, cashierAppRoutes, ppobAppRoutes, kanbanAppRoutes, fileAppRoutes, newsAppRoutes, trainingAppRoutes, bankTransferAppRoutes];

// pages
const customPagesRoutes = {
  path: "/pages",
  name: "Pages",
  icon: "pages",
  header: "Custom",
  children: [
    {
      path: "/pages/starter",
      name: "Starter",
      element: <Starter />,
      route: PermissionRoute,
    },
    {
      path: "/pages/timeline",
      name: "Timeline",
      element: <Timeline />,
      route: PermissionRoute,
    },
    {
      path: "/pages/invoice",
      name: "Invoice",
      element: <Invoice />,
      route: PermissionRoute,
    },
    {
      path: "/pages/gallery",
      name: "Gallery",
      element: <Gallery />,
      route: PermissionRoute,
    },
    {
      path: "/pages/profile",
      name: "Profile",
      element: <Profile />,
      route: PrivateRoute,
    },
    {
      path: "/pages/faqs",
      name: "FAQs",
      element: <FAQs />,
      route: PermissionRoute,
    },
    {
      path: "/pages/pricing",
      name: "Pricing",
      element: <Pricing />,
      route: PermissionRoute,
    },
  ],
};

// settings routes
const settingsRoutes = {
  path: "/settings",
  name: "Settings",
  icon: "mgc_settings_3_line",
  header: "Configuration",
  children: [
    {
      path: "/settings/cooperative/general-info",
      name: "Informasi Umum Koperasi",
      element: <CooperativeGeneralSettings />,
      route: PermissionRoute,
    },
    {
      path: "/settings/cooperative/accounting",
      name: "Pengaturan Akuntansi & Periode",
      element: <AccountingSettings />,
      route: PermissionRoute,
    },
    {
      path: "/settings/cooperative/membership",
      name: "Pengaturan Keanggotaan",
      element: <MembershipSettings />,
      route: PermissionRoute,
    },
    {
      path: "/settings/cooperative/outlet",
      name: "Pengaturan Outlet",
      element: <OutletSettings />,
      route: PermissionRoute,
    },
    {
      path: "/settings/cooperative/savings",
      name: "Pengaturan Simpanan",
      element: <SavingsSettings />,
      route: PermissionRoute,
    },
    {
      path: "/settings/cooperative/loans",
      name: "Pengaturan Pinjaman",
      element: <LoanSettings />,
      route: PermissionRoute,
    },
    {
      path: "/settings/cooperative/shu",
      name: "Pengaturan SHU",
      element: <SHUSettings />,
      route: PermissionRoute,
    },
  ],
};

// tables
const tableRoutes = {
  path: "/tables",
  name: "Tables",
  icon: "table",
  header: "Elements",
  children: [
    {
      path: "/ui/tables/basic-tables",
      name: "Basic Tables",
      element: <BasicTables />,
      route: PermissionRoute,
    },
    {
      path: "/ui/tables/data-tables",
      name: "Data Tables",
      element: <DataTables />,
      route: PermissionRoute,
    },
  ]
}

// ui
const uiRoutes = {
  path: "/ui",
  name: "Components",
  icon: "pocket",
  header: "Elements",
  children: [
    {
      path: "/ui/components",
      name: "Components",
      children: [
        {
          path: "/ui/accordions",
          name: "Accordions",
          element: <Accordions />,
          route: PermissionRoute,
        },
        {
          path: "/ui/alerts",
          name: "Alerts",
          element: <Alerts />,
          route: PermissionRoute,
        },
        {
          path: "/ui/avatars",
          name: "Avatars",
          element: <Avatars />,
          route: PermissionRoute,
        },
        {
          path: "/ui/buttons",
          name: "Buttons",
          element: <Buttons />,
          route: PermissionRoute,
        },
        {
          path: "/ui/badges",
          name: "Badges",
          element: <Badges />,
          route: PermissionRoute,
        },
        {
          path: "/ui/breadcrumb",
          name: "Breadcrumb",
          element: <Breadcrumb />,
          route: PermissionRoute,
        },
        {
          path: "/ui/cards",
          name: "Cards",
          element: <Cards />,
          route: PermissionRoute,
        },
        {
          path: "/ui/collapse",
          name: "Collapse",
          element: <Collapse />,
          route: PermissionRoute,
        },
        {
          path: "/ui/dismissible",
          name: "Dismissible",
          element: <Dismissible />,
          route: PermissionRoute,
        },
        {
          path: "/ui/dropdowns",
          name: "Dropdowns",
          element: <Dropdowns />,
          route: PermissionRoute,
        },
        {
          path: "/ui/progress",
          name: "Progress",
          element: <Progress />,
          route: PermissionRoute,
        },
        {
          path: "/ui/skeleton",
          name: "Skeleton",
          element: <Skeleton />,
          route: PermissionRoute,
        },
        {
          path: "/ui/spinners",
          name: "Spinners",
          element: <Spinners />,
          route: PermissionRoute,
        },
        {
          path: "/ui/list-group",
          name: "List Group",
          element: <ListGroup />,
          route: PermissionRoute,
        },
        {
          path: "/ui/ratio",
          name: "Ratio",
          element: <Ratio />,
          route: PermissionRoute,
        },
        {
          path: "/ui/tab",
          name: "Tab",
          element: <Tabs />,
          route: PermissionRoute,
        },
        {
          path: "/ui/modals",
          name: "Modals",
          element: <Modals />,
          route: PermissionRoute,
        },
        {
          path: "/ui/offcanvas",
          name: "Offcanvas",
          element: <Offcanvas />,
          route: PermissionRoute,
        },
        {
          path: "/ui/popovers",
          name: "Popovers",
          element: <Popovers />,
          route: PermissionRoute,
        },
        {
          path: "/ui/tooltips",
          name: "Tooltips",
          element: <Tooltips />,
          route: PermissionRoute,
        },
        {
          path: "/ui/typography",
          name: "Typography",
          element: <Typography />,
          route: PermissionRoute,
        },
      ],
    },
    {
      path: '/extended',
      name: 'Extended',
      children: [
        {
          path: "/extended/swiper",
          name: "Swiper",
          element: <Swiper />,
          route: PermissionRoute,
        },
        {
          path: "/extended/nestable-list",
          name: "Nestable List",
          element: <NestableList />,
          route: PermissionRoute,
        },
        {
          path: "/extended/ratings",
          name: "Ratings",
          element: <Ratings />,
          route: PermissionRoute,
        },
        {
          path: "/extended/animation",
          name: "Animation",
          element: <Animation />,
          route: PermissionRoute,
        },
        {
          path: "/extended/player",
          name: "Player",
          element: <Player />,
          route: PermissionRoute,
        },
        {
          path: "/extended/scrollbar",
          name: "Scrollbar",
          element: <Scrollbar />,
          route: PermissionRoute,
        },
        {
          path: "/extended/sweet-alert",
          name: "Sweet Alert",
          element: <SweetAlert />,
          route: PermissionRoute,
        },
        {
          path: "/extended/tour",
          name: "Tourpage",
          element: <TourPage />,
          route: PermissionRoute,
        },
        {
          path: "/extended/tooltippy",
          name: "Tippy Tooltip",
          element: <TippyTooltip />,
          route: PermissionRoute,
        },
        {
          path: "/extended/lightbox",
          name: "Lightbox",
          element: <Lightbox />,
          route: PermissionRoute,
        },
      ],
    },
    {
      path: '/ui/forms',
      name: 'Forms',
      children: [
        {
          path: "/ui/forms/form-elements",
          name: "Form Elements",
          element: <FormElements />,
          route: PermissionRoute,
        },
        {
          path: "/ui/forms/select",
          name: "Select",
          element: <FormSelect />,
          route: PermissionRoute,
        },
        {
          path: "/ui/forms/range",
          name: "Range",
          element: <Range />,
          route: PermissionRoute,
        },
        {
          path: "/ui/forms/pickers",
          name: "Pickers",
          element: <Pickers />,
          route: PermissionRoute,
        },
        {
          path: "/ui/forms/masks",
          name: "Masks",
          element: <Masks />,
          route: PermissionRoute,
        },
        {
          path: "/ui/forms/editor",
          name: "Editor",
          element: <Editor />,
          route: PermissionRoute,
        },
        {
          path: "/ui/forms/file-uploads",
          name: "File Uploads",
          element: <FileUploads />,
          route: PermissionRoute,
        },
        {
          path: "/ui/forms/validation",
          name: "Validation",
          element: <Validation />,
          route: PermissionRoute,
        },
        {
          path: "/ui/forms/layout",
          name: "Layout",
          element: <FormLayout />,
          route: PermissionRoute,
        },
      ],
    },
    {
      path: '/ui/icons',
      name: 'Icons',
      children: [
        {
          path: "/ui/icons/mingcute",
          name: "MingCute",
          element: <MingCuteIcons />,
          route: PermissionRoute,
        },
        {
          path: "/ui/icons/feather",
          name: "Feather",
          element: <FeatherIcons />,
          route: PermissionRoute,
        },
        {
          path: "/ui/icons/material-symbols",
          name: "Material Symbols", 
          element: <MaterialSymbolIcons />,
          route: PermissionRoute,
        },
      ],
    },
    {
      path: '/ui/maps',
      name: 'Maps',
      children: [
        {
          path: "/ui/maps/vector-maps",
          name: "Vector Maps",
          element: <VectorMaps />,
          route: PermissionRoute,
        },
        {
          path: "/ui/maps/google-maps",
          name: "Google Maps",
          element: <GoogleMaps />,
          route: PermissionRoute,
        },
      ],
    },
  ],
};

const chartRoutes = {
  path: "/ui/chart",
  name: "Charts",
  route: PermissionRoute,
  roles: ["Admin"],
  icon: "chart",
  element: <Chart />,
  header: "Elements",
};

// auth
const authRoutes = [
  {
    path: "/auth/login",
    name: "Login",
    element: <Login />,
    route: Route,
  },
  {
    path: "/auth/register",
    name: "Register",
    element: <Register />,
    route: Route,
  },
  {
    path: "/auth/cooperative-register",
    name: "Cooperative Register",
    element: <CooperativeRegister />,
    route: Route,
  },
  {
    path: "/auth/recover-password",
    name: "Recover Password",
    element: <RecoverPassword />,
    route: Route,
  },
  {
    path: "/auth/lock-screen",
    name: "Lock Screen",
    element: <LockScreen />,
    route: Route,
  },
];

// public routes
const otherPublicRoutes = [
  {
    path: "/privacy-policy",
    name: "Privacy Policy",
    element: <PrivacyPolicy />,
    route: Route,
  },
  {
    path: "/terms-of-service",
    name: "Terms of Service", 
    element: <TermsOfService />,
    route: Route,
  },
  {
    path: "/data-deletion",
    name: "Data Deletion", 
    element: <DataDeletion />,
    route: Route,
  },
  {
    path: "*",
    name: "Error - 404",
    element: <Error404 />,
    route: Route,
  },
  {
    path: "/maintenance",
    name: "Maintenance",
    element: <Maintenance />,
    route: Route,
  },
  {
    path: "/coming-soon",
    name: "Coming Soon",
    element: <ComingSoon />,
    route: Route,
  },
  {
    path: "/error-404",
    name: "Error - 404",
    element: <Error404 />,
    route: Route,
  },
  {
    path: "/error-403",
    name: "Error - 403 Forbidden",
    element: <Error403 />,
    route: Route,
  },
  {
    path: "/error-500",
    name: "Error - 500",
    element: <Error500 />,
    route: Route,
  },
];

// flatten the list of all nested routes
const flattenRoutes = (routes) => {
  let flatRoutes = [];

  routes = routes || [];
  routes.forEach((item) => {
    flatRoutes.push(item);
    if (typeof item.children !== "undefined") {
      flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)];
    }
  });
  return flatRoutes;
};

// All routes
const authProtectedRoutes = [
  dashboardRoutes,
  ...appRoutes,
  adminRoutes,
  settingsRoutes,
  customPagesRoutes,
  tableRoutes,
  uiRoutes,
  chartRoutes,
];
const publicRoutes = [...authRoutes, ...otherPublicRoutes];

const authProtectedFlattenRoutes = flattenRoutes([...authProtectedRoutes]);
const publicProtectedFlattenRoutes = flattenRoutes([...publicRoutes]);
export {
  publicRoutes,
  authProtectedRoutes,
  authProtectedFlattenRoutes,
  publicProtectedFlattenRoutes,
};
